import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useVehicleSettingsService } from "../service/vehicleSettingsService";
import { useSnackbar } from "notistack";

export const useVehicleSettingsOperations = () => {
  const vehicleSettingsService = useVehicleSettingsService();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  
  // ==== Hooks para Puntos Críticos ====
  
  // Query para obtener puntos críticos
  const useCriticalPoints = (params = {}) => {
    return useQuery({
      queryKey: ['criticalPoints', params],
      queryFn: () => vehicleSettingsService.getCriticalPoints(params),
      keepPreviousData: true,
    });
  };
  
  // Query para obtener un punto crítico por ID
  const useCriticalPoint = (id) => {
    return useQuery({
      queryKey: ['criticalPoint', id],
      queryFn: () => id ? vehicleSettingsService.getCriticalPointById(id) : null,
      enabled: !!id,
    });
  };
  
  // Mutation para crear un punto crítico
  const useCreateCriticalPoint = () => {
    return useMutation({
      mutationFn: (pointData) => vehicleSettingsService.createCriticalPoint(pointData),
      onSuccess: () => {
        queryClient.invalidateQueries(['criticalPoints']);
        enqueueSnackbar('Punto crítico creado correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al crear punto crítico: ${error.message || 'Error desconocido'}`, { variant: 'error' });
      }
    });
  };
  
  // Mutation para actualizar un punto crítico
  const useUpdateCriticalPoint = () => {
    return useMutation({
      mutationFn: ({ id, data }) => vehicleSettingsService.updateCriticalPoint(id, data),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['criticalPoints']);
        queryClient.invalidateQueries(['criticalPoint', variables.id]);
        enqueueSnackbar('Punto crítico actualizado correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al actualizar punto crítico: ${error.message || 'Error desconocido'}`, { variant: 'error' });
      }
    });
  };
  
  // Mutation para eliminar un punto crítico
  const useDeleteCriticalPoint = () => {
    return useMutation({
      mutationFn: (id) => vehicleSettingsService.deleteCriticalPoint(id),
      onSuccess: () => {
        queryClient.invalidateQueries(['criticalPoints']);
        enqueueSnackbar('Punto crítico eliminado correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al eliminar punto crítico: ${error.message || 'Error desconocido'}`, { variant: 'error' });
      }
    });
  };
  
  // Mutation para cambiar estado de un punto crítico
  const useToggleCriticalPointStatus = () => {
    return useMutation({
      mutationFn: ({ id, isActive }) => vehicleSettingsService.toggleCriticalPointStatus(id, isActive),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['criticalPoints']);
        queryClient.invalidateQueries(['criticalPoint', variables.id]);
        enqueueSnackbar(`Punto crítico ${variables.isActive ? 'activado' : 'desactivado'} correctamente`, { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al cambiar estado del punto crítico: ${error.message || 'Error desconocido'}`, { variant: 'error' });
      }
    });
  };
  
  // ==== Hooks para Rutas Predefinidas ====
  
  // Query para obtener rutas predefinidas
  const useRoutes = (params = {}) => {
    return useQuery({
      queryKey: ['predefinedRoutes', params],
      queryFn: () => vehicleSettingsService.getRoutes(params),
      keepPreviousData: true,
    });
  };
  
  // Query para obtener una ruta por ID
  const useRoute = (id) => {
    return useQuery({
      queryKey: ['predefinedRoute', id],
      queryFn: () => id ? vehicleSettingsService.getRouteById(id) : null,
      enabled: !!id,
    });
  };
  
  // Query para obtener clientes para filtro
  const useRouteClients = () => {
    return useQuery({
      queryKey: ['routeClients'],
      queryFn: () => vehicleSettingsService.getRouteClients(),
    });
  };
  
  // Mutation para crear una ruta
  const useCreateRoute = () => {
    return useMutation({
      mutationFn: (routeData) => vehicleSettingsService.createRoute(routeData),
      onSuccess: () => {
        queryClient.invalidateQueries(['predefinedRoutes']);
        queryClient.invalidateQueries(['routeClients']);
        enqueueSnackbar('Ruta predefinida creada correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al crear ruta predefinida: ${error.message || 'Error desconocido'}`, { variant: 'error' });
      }
    });
  };
  
  // Mutation para actualizar una ruta
  const useUpdateRoute = () => {
    return useMutation({
      mutationFn: ({ id, data }) => vehicleSettingsService.updateRoute(id, data),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['predefinedRoutes']);
        queryClient.invalidateQueries(['predefinedRoute', variables.id]);
        queryClient.invalidateQueries(['routeClients']);
        enqueueSnackbar('Ruta predefinida actualizada correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al actualizar ruta predefinida: ${error.message || 'Error desconocido'}`, { variant: 'error' });
      }
    });
  };
  
  // Mutation para eliminar una ruta
  const useDeleteRoute = () => {
    return useMutation({
      mutationFn: (id) => vehicleSettingsService.deleteRoute(id),
      onSuccess: () => {
        queryClient.invalidateQueries(['predefinedRoutes']);
        queryClient.invalidateQueries(['routeClients']);
        enqueueSnackbar('Ruta predefinida eliminada correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al eliminar ruta predefinida: ${error.message || 'Error desconocido'}`, { variant: 'error' });
      }
    });
  };
  
  // Mutation para cambiar estado de una ruta
  const useToggleRouteStatus = () => {
    return useMutation({
      mutationFn: ({ id, active }) => vehicleSettingsService.toggleRouteStatus(id, active),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['predefinedRoutes']);
        queryClient.invalidateQueries(['predefinedRoute', variables.id]);
        enqueueSnackbar(`Ruta predefinida ${variables.active ? 'activada' : 'desactivada'} correctamente`, { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al cambiar estado de la ruta predefinida: ${error.message || 'Error desconocido'}`, { variant: 'error' });
      }
    });
  };
  
  // ==== Hooks para Proveedores Satelitales ====
  
  // Query para obtener proveedores satelitales
  const useSatelliteProviders = () => {
    return useQuery({
      queryKey: ['satelliteProviders'],
      queryFn: () => vehicleSettingsService.getSatelliteProviders(),
    });
  };
  
  // Query para obtener configuración de un proveedor
  const useProviderConfig = (providerName) => {
    return useQuery({
      queryKey: ['providerConfig', providerName],
      queryFn: () => providerName ? vehicleSettingsService.getProviderConfig(providerName) : null,
      enabled: !!providerName,
    });
  };
  
  // Mutation para actualizar configuración de un proveedor
  const useUpdateProviderConfig = () => {
    return useMutation({
      mutationFn: ({ providerName, configData }) => vehicleSettingsService.updateProviderConfig(providerName, configData),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['satelliteProviders']);
        queryClient.invalidateQueries(['providerConfig', variables.providerName]);
        enqueueSnackbar('Configuración de proveedor actualizada correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al actualizar configuración de proveedor: ${error.message || 'Error desconocido'}`, { variant: 'error' });
      }
    });
  };
  
  // Mutation para actualizar destinatarios de un proveedor
  const useUpdateProviderRecipients = () => {
    return useMutation({
      mutationFn: ({ providerName, recipients }) => vehicleSettingsService.updateProviderRecipients(providerName, recipients),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['providerConfig', variables.providerName]);
        enqueueSnackbar('Destinatarios de proveedor actualizados correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al actualizar destinatarios: ${error.message || 'Error desconocido'}`, { variant: 'error' });
      }
    });
  };
  
  // Mutation para procesar notificaciones
  const useProcessNotifications = () => {
    return useMutation({
      mutationFn: (providerName) => vehicleSettingsService.processNotifications(providerName),
      onSuccess: () => {
        enqueueSnackbar('Notificaciones procesadas correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al procesar notificaciones: ${error.message || 'Error desconocido'}`, { variant: 'error' });
      }
    });
  };

  // ==== Hooks para configuracion del reporte automatico para la ubicacion de los vehiculos ====
  const useLocationReportConfig = (selectedDate = null) => {
    return useQuery({
      queryKey: ['locationReportConfig', selectedDate],
      queryFn: () => vehicleSettingsService.getLocationReportConfig(selectedDate),
      // Revalidar cuando cambie la fecha
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutos
    });
  };
  
  const useUpdateReportVehicles = () => {
    return useMutation({
      mutationFn: (vehicles) => vehicleSettingsService.updateReportVehicles(vehicles),
      onSuccess: () => {
        queryClient.invalidateQueries(['locationReportConfig']);
        enqueueSnackbar('Vehículos actualizados correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al actualizar vehículos: ${error.message}`, { variant: 'error' });
      }
    });
  };
  
  const useUpdateReportSchedules = () => {
    return useMutation({
      mutationFn: (schedules) => vehicleSettingsService.updateReportSchedules(schedules),
      onSuccess: () => {
        queryClient.invalidateQueries(['locationReportConfig']);
        enqueueSnackbar('Horarios actualizados correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al actualizar horarios: ${error.message}`, { variant: 'error' });
      }
    });
  };
  
  const useProcessLocationReports = () => {
    return useMutation({
      mutationFn: () => vehicleSettingsService.processLocationReports(),
      onSuccess: () => {
        enqueueSnackbar('Proceso de reportes iniciado correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al procesar reportes: ${error.message}`, { variant: 'error' });
      }
    });
  };
  
  const useUpdateVehicleOperator = () => {
    return useMutation({
      mutationFn: ({ vehicleId, operatorData }) => 
        vehicleSettingsService.updateVehicleOperator(vehicleId, operatorData),
      onSuccess: () => {
        queryClient.invalidateQueries(['locationReportConfig']);
        enqueueSnackbar('Configuración de vehículo actualizada', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al actualizar configuración: ${error.message}`, { variant: 'error' });
      }
    });
    
  };
  
  const useConfigureVehicleWithOperator = () => {
    return useMutation({
      mutationFn: (vehicleData) => 
        vehicleSettingsService.configureVehicleWithOperator(vehicleData),
      onSuccess: () => {
        queryClient.invalidateQueries(['locationReportConfig']);
        enqueueSnackbar('Vehículo configurado correctamente', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error al configurar vehículo: ${error.message}`, { variant: 'error' });
      }
    });
  };
  return {
    // Critical Points
    useCriticalPoints,
    useCriticalPoint,
    useCreateCriticalPoint,
    useUpdateCriticalPoint,
    useDeleteCriticalPoint,
    useToggleCriticalPointStatus,
    
    // Predefined Routes
    useRoutes,
    useRoute,
    useRouteClients,
    useCreateRoute,
    useUpdateRoute,
    useDeleteRoute,
    useToggleRouteStatus,
    
    // Satellite Providers
    useSatelliteProviders,
    useProviderConfig,
    useUpdateProviderConfig,
    useUpdateProviderRecipients,
    useProcessNotifications,

    // configuration report Providers
    useLocationReportConfig,
    useUpdateReportVehicles,
    useUpdateReportSchedules,
    useProcessLocationReports,
    useUpdateVehicleOperator,
    useConfigureVehicleWithOperator
  };
};